<script setup>
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'

/**
 * Renders a PDF (a blob URL) page by page into canvases with pdf.js (D-104).
 *
 * A comparison of revisions (D-152) prints an invisible token "CMP<n>Q" where change n shows:
 * with `markers`, each page's text is searched for them, a numbered badge is drawn in the
 * page's margin beside each (`markers[n]` is the badge's text), and `found` reports
 * {n: page}. `show(page)` brings a page into view.
 */
// `columns`: pages side by side (2: a spread, as a printed booklet).
const props = defineProps({ src: { type: String, required: true }, markers: { type: Object, default: null }, columns: { type: Number, default: 1 } })
const emit = defineEmits(['found'])
const host = ref(null)
const error = ref('')
const loading = ref(true)
let task = null
const TOKEN = /C\s*M\s*P\s*(\d+)\s*Q/g

async function render() {
  loading.value = true
  error.value = ''
  host.value.innerHTML = ''
  try {
    // The legacy build: pdf.js 5 otherwise needs the newest browsers (URL.parse, Promise.try).
    const pdfjs = await import('pdfjs-dist/legacy/build/pdf.mjs')
    const worker = await import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?url')
    pdfjs.GlobalWorkerOptions.workerSrc = worker.default
    task = pdfjs.getDocument({ url: props.src })
    const pdf = await task.promise
    const columns = Math.max(1, props.columns)
    const width = columns > 1 ? Math.floor(((host.value.clientWidth || 1200) - 16 * (columns - 1)) / columns) : Math.min(host.value.clientWidth || 800, 1000)
    const found = {}
    for (let n = 1; n <= pdf.numPages; n++) {
      const page = await pdf.getPage(n)
      const base = page.getViewport({ scale: 1 })
      const ratio = window.devicePixelRatio || 1
      // A spread: as large as both pages fit the window's height.
      const pageWidth = columns > 1 ? Math.min(width, Math.floor(window.innerHeight * 0.68 * (base.width / base.height))) : width
      const viewport = page.getViewport({ scale: (pageWidth / base.width) * ratio })
      const canvas = document.createElement('canvas')
      canvas.width = viewport.width
      canvas.height = viewport.height
      canvas.style.width = `${pageWidth}px`
      canvas.className = columns > 1 ? 'block rounded shadow' : 'mx-auto mb-3 block rounded shadow'
      canvas.dataset.page = String(n)
      host.value.appendChild(canvas)
      const ctx = canvas.getContext('2d')
      await page.render({ canvasContext: ctx, viewport }).promise
      if (props.markers) await mark(page, viewport, ctx, n, ratio, found)
    }
    if (props.markers) emit('found', found)
  } catch (e) {
    error.value = e?.message || 'PDF'
  } finally {
    loading.value = false
  }
}

/** The page's tokens: a badge in the left margin at the height of each, its number from `markers`. */
async function mark(page, viewport, ctx, n, ratio, found) {
  const text = await page.getTextContent()
  const placed = []
  // The page's text in one string (tiny text may come in pieces), each character knowing its item.
  let all = ''
  const owner = []
  for (const item of text.items) {
    const str = String(item.str || '')
    for (let k = 0; k < str.length; k++) owner.push(item)
    all += str
  }
  for (const m of all.matchAll(TOKEN)) {
    const item = owner[m.index]
    const i = Number(m[1])
    if (found[i] === undefined) found[i] = n
    const label = props.markers[i]
    if (label === undefined) continue
    let [, y] = viewport.convertToViewportPoint(item.transform[4], item.transform[5])
    // Badges of changes on one line stand one under another.
    while (placed.some((p) => Math.abs(p - y) < 18 * ratio)) y += 18 * ratio
    placed.push(y)
    const r = 8 * ratio
    const x = 14 * ratio
    ctx.fillStyle = '#dc2626'
    ctx.beginPath()
    ctx.arc(x, y, r, 0, Math.PI * 2)
    ctx.fill()
    ctx.fillStyle = '#fff'
    ctx.font = `bold ${9 * ratio}px system-ui, sans-serif`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(String(label), x, y + 0.5 * ratio)
  }
}

function show(n) {
  const canvas = host.value?.querySelector(`canvas[data-page="${n}"]`)
  canvas?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  canvas?.animate?.([{ outline: '3px solid #dc2626' }, { outline: '3px solid transparent' }], { duration: 1200 })
}
defineExpose({ show })
onMounted(render)
watch(() => props.src, render)
onBeforeUnmount(() => task?.destroy?.())
</script>

<template>
  <div>
    <p v-if="loading" class="py-6 text-center text-faint"><i class="fa-solid fa-circle-notch fa-spin" aria-hidden="true"></i></p>
    <p v-if="error" class="text-sm text-red-600">{{ error }}</p>
    <div ref="host" :class="['w-full', columns > 1 ? 'grid gap-4' : '']" :style="columns > 1 ? { gridTemplateColumns: `repeat(${columns}, max-content)`, justifyContent: 'center' } : undefined"></div>
  </div>
</template>
